import React, {
  ComponentType,
  ElementType,
  MouseEventHandler,
  PureComponent,
  ReactNode,
  createRef,
  forwardRef,
} from 'react';
import styled, { css } from 'styled-components';
import { width as ssWidth } from 'styled-system';

import Box from 'components/Box';
import { bp, colors, fontWeights } from 'theme';

const hexToDecimalArray = (color: string) => {
  const digits = color.replace('#', '');
  return new Array(3)
    .fill(0)
    .map((item, index) => parseInt(digits.substr(index * 2, 2), 16));
};

const decimalArrayToHex = (color: Array<number>) => {
  const hexString = color
    .map((group) => Math.round(group).toString(16))
    .join('');
  return `#${hexString}`;
};

const getWidth = (width: string | number | Array<string | number>) => {
  if (width instanceof Array) {
    const breakpoint = bp.reduce(
      (acc, point, index) =>
        width instanceof Array && window.innerWidth >= point && width[index]
          ? index
          : acc,
      0
    );
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | number' is not assignab... Remove this comment to see the full error message
    return width instanceof Array ? parseInt(width[breakpoint], 10) : 0;
  }

  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | number' is not assignab... Remove this comment to see the full error message
  return parseInt(width, 10);
};

export const defaultButtonStyles = css<{
  lightColor?: boolean;
  greyColor?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 15px;
  height: 15px;
  box-shadow: 0 0 0 2px currentColor inset;
  border-radius: 50%;
  color: ${colors.waterBlue};
  font-size: 10px;
  font-weight: ${fontWeights.semiBold};
  transition: all 0.3s ease;
  &::after {
    content: '?';
  }
  ${(props) =>
    props.lightColor &&
    `
    color: white;
  `}
  ${(props) =>
    props.greyColor &&
    `
    color: ${colors.battleshipGrey};
  `}
`;
export const DefaultButton = styled(Box)`
  ${defaultButtonStyles}
`;
export const IconButton = styled(Box)`
  color: ${colors.waterBlue};
`;
const TooltipContainer = styled.div<{ triangleColor: string }>`
  position: relative;
  z-index: 103;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    z-index: 1;
    transform: translate(-16px, -5px);
    width: 0;
    height: 0;
    border: 8px solid transparent;
    ${(props) => css`
      border-top-color: ${props.triangleColor};
      border-right-color: ${props.triangleColor};
    `}
  }
`;
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TooltipBox = styled.div<{ right: number; width?: any }>`
  position: absolute;
  transform: translate3d(0, -100%, 0);
  top: -20px;
  right: ${(props) => props.right}px;
  padding: 20px 20px 27px;
  width: 300px;
  background-color: white;
  background-image: linear-gradient(
    to left,
    ${colors.skyBlue},
    ${colors.niceBlue}
  );
  background-size: 100% 7px;
  background-repeat: no-repeat;
  background-position: left bottom;
  box-shadow: 0 5px 12px 2px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  color: ${colors.battleshipGrey};
  font-size: 14px;
  text-align: left;
  ${ssWidth}
`;
const Button = styled(
  forwardRef(
    (
      {
        component: Component,
        triangleColor,
        ...props
      }: {
        type?: string;
        tabIndex?: number | string;
        onClick?: MouseEventHandler;
        disabled?: boolean;
        children: ReactNode;
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        component: ComponentType<any> | string;
        triangleColor?: string;
      },
      ref
    ) => (
      // eslint-disable-next-line react/jsx-props-no-spreading, @typescript-eslint/no-explicit-any
      <Component ref={ref as any} {...props} />
    )
  )
)`
  display: block;
  padding: 0;
  overflow: visible;
  appearance: none;
  border: 0;
  outline: 0;
  background: transparent;
  transition: all 0.3s ease;
  &:hover,
  &:focus {
    ${TooltipContainer} {
      opacity: 1;
      visibility: visible;
    }
    ${DefaultButton} {
      color: white;
      background: ${colors.waterBlue};
      box-shadow: 0 0 0 2px ${colors.waterBlue} inset;
    }
    ${IconButton} {
      color: ${colors.niceBlue};
    }
  }
`;
type State = {
  positionRight: number;
  triangleColor: string;
};
type Props = {
  children: ReactNode;
  'data-modal-focus'?: 'true' | 'false';
  button?: ReactNode;
  width?: number | string | Array<number | string>;
  component?: ElementType;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (...args: Array<any>) => any;
  disabled?: boolean;
  translateY?: number;
  lightColor?: boolean;
  greyColor?: boolean;
};
export default class Tooltip extends PureComponent<Props, State> {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/state-in-constructor
  state = {
    positionRight: 0,
    triangleColor: colors.skyBlue,
  };

  buttonRef = createRef<Element>();

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/sort-comp
  resizeHandler = () => {
    if (this.buttonRef.current) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/destructuring-assignment
      const tooltipWidth = this.props.width ? getWidth(this.props.width) : 300;
      const rect = this.buttonRef.current.getBoundingClientRect();
      const overflowWidth = tooltipWidth - rect.width;
      let positionRight = 0;

      if (rect.left - overflowWidth < 10) {
        positionRight = rect.left - overflowWidth - 10;
      } else {
        positionRight = 0;
      }

      const gradient = {
        a: hexToDecimalArray(colors.niceBlue),
        b: hexToDecimalArray(colors.skyBlue),
      };
      const triangleColorArray = gradient.a.map(
        (g, index) =>
          gradient.a[index] +
          (1 + positionRight / tooltipWidth) *
            (gradient.b[index] - gradient.a[index])
      );
      const triangleColor = decimalArrayToHex(triangleColorArray);
      this.setState({
        positionRight,
        triangleColor,
      });
    }
  };

  componentDidMount() {
    if (window) {
      window.addEventListener('resize', this.resizeHandler);
      this.resizeHandler();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeHandler);
  }

  render() {
    const {
      onClick,
      disabled,
      component,
      children,
      width,
      button,
      lightColor,
      greyColor,
      ...props
    } = this.props;
    return (
      <Button
        ref={this.buttonRef}
        type={!component || component === 'button' ? 'button' : undefined}
        component={component || 'button'}
        tabIndex="0"
        aria-label={getText('Show tooltip')}
        onClick={onClick}
        disabled={disabled}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {/* TODO: Fix this the next time the file is edited. */}
        {/* eslint-disable-next-line react/destructuring-assignment */}
        <TooltipContainer triangleColor={this.state.triangleColor}>
          {/* TODO: Fix this the next time the file is edited. */}
          {/* eslint-disable-next-line react/destructuring-assignment */}
          <TooltipBox right={this.state.positionRight} width={width}>
            {children}
          </TooltipBox>
        </TooltipContainer>
        <div>
          {button || (
            <DefaultButton lightColor={lightColor} greyColor={greyColor} />
          )}
        </div>
      </Button>
    );
  }
}
